import React, { useCallback, useEffect, useState } from "react";
import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import styles from "./DocumentList.module.scss";
import {
  getContactDocumentsList,
  getClientDocumentsList,
  getSupplierDocumentsList,
  getDocument,
  updateContactDocumentItem,
  updateClientDocumentItem,
  updateSupplierDocumentItem,
} from "api";
import { DateFormatCell } from "components/common/formatter";
import { Button } from "@progress/kendo-react-buttons";
import { process } from "@progress/kendo-data-query";
import ClientDocumentModal from "./AddEditDocument";
import fileIcon from "components/common/fileIcon";
import { FileDragDrop } from "pages/CaseFile/CaseActivities/CaseActivitiesPage";
import DocumentViewer from "components/common/DocumentViewer";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-toastify";

const documentRegisterId = (row, component) => {
  if (!row) return undefined;
  if (component === "supplier") {
    return row?.supplierDocumentID;
  }
  if (component === "contact") {
    return row?.contactDocumentID
  }
  return row?.clientDocumentID;
};

export const DocumentList = ({
  clientId,
  supplierId,
  supplierRef,
  contactId,
  tileHeading,
  viewMode,
  component,
  canModify = false,
  className = "",
}) => {
  const isSupplier = supplierId != null;
  const isContact = component === "contact";
  const heading =
    tileHeading ?? (isSupplier ? "Documents" : "Document Register");
  const [list, setLists] = useState({ data: [], total: 50 });
  const [showDocRegister, setShowDocRegister] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [viewId, setViewId] = useState();
  const uploadRef = React.createRef();
  const [showDocument, setShowDocument] = useState(false);
  const [documentViewerData, setDocumentViewerData] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docRowToDelete, setDocRowToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      if (supplierId != null) {
        const data = await getSupplierDocumentsList(supplierId);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      } else if (isContact && contactId) {
        const data = await getContactDocumentsList(contactId);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      } else if (clientId) {
        const data = await getClientDocumentsList(clientId?.clientId);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      }
    } catch (error) { }
  }, [clientId, supplierId, contactId, isContact]);

  const [dataState, setDataState] = React.useState({
    take: 100,
    skip: 0,
    sort: [
      {
        field: "documentDate",
        dir: "asc",
      },
    ],
  });

  const showDocumentFile = async (id) => {
    const { data } = await getDocument(id);
    setDocumentViewerData([data.data]);
    setShowDocument(true);
  };

  const ViewDocs = (props) => {
    return (
      <td>
        <Button
          onClick={() => {
            setViewId(props.dataItem);
            setShowDocRegister(true);
          }}
          className="button-small"
        >
          View
        </Button>
      </td>
    );
  };

  const handleDeleteClick = (dataItem) => {
    const regId = documentRegisterId(dataItem, component);
    if (!regId) return;
    setDocRowToDelete(dataItem);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDocument = async () => {
    const regId = documentRegisterId(docRowToDelete, component);
    if (!regId) {
      setShowDeleteConfirm(false);
      setDocRowToDelete(null);
      return;
    }
    setDeletingDoc(true);
    try {
      const payload = { data: { active: false } };
      if (isSupplier) {
        await updateSupplierDocumentItem(payload, supplierId, regId);
      } else if (isContact) {
        await updateContactDocumentItem(payload, contactId, regId);
      } else {
        await updateClientDocumentItem(payload, clientId?.clientId, regId);
      }
      toast.success("Document removed");
      await fetchDocs();
    } catch (err) {
      console.error(err);
    } finally {
      setShowDeleteConfirm(false);
      setDocRowToDelete(null);
      setDeletingDoc(false);
    }
  };

  const DeleteDocCell = (props) => {
    const regId = documentRegisterId(props.dataItem, component);
    if (!regId) {
      return <td />;
    }
    return (
      <td className="text-center">
        <Button
          className="btn m-0 p-0 border-0 me-3"
          title="Remove document"
          disabled={component === "supplier" ? false : !canModify}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(props.dataItem);
          }}
        >
          <RxCross2 size={15} color="red" aria-hidden />
        </Button>
      </td>
    );
  };

  const DocumentCell = (e) => {
    const { documentId, documentName } = e.dataItem;
    return documentId ? (
      <td className="p-1" {...e} onClick={() => showDocumentFile(documentId)}>
        <div role="button" className="d-flex">
          {fileIcon(documentName)}

          <span className="ps-2 fs-sm d-lg-none d-xl-block">
            {documentName?.length > 20
              ? documentName.substring(0, 20)
              : documentName}
          </span>

          <span className="ps-2 fs-sm d-xl-none d-lg-block">
            {documentName?.split(".").pop()}
          </span>
        </div>
      </td>
    ) : (
      <td></td>
    );
  };

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState?.sort?.[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState?.sort?.[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  const handleDrop = (event) => {
    setDroppedFiles([...event]);
    setShowDocRegister(true);
  };

  useEffect(() => {
    if (supplierId != null || clientId || (isContact && contactId)) {
      fetchDocs();
    }
  }, [fetchDocs, clientId, supplierId, isContact, contactId]);

  return (
    <div className={className}>
      <Tile
        heading={heading}
        className="py-2 px-4 overflow-auto"
        headingItems={
          <Button
            className="button-small bgBtnSecondary fw-bold me-1"
            disabled={component === "supplier" ? false : !canModify}
            onClick={() => {
              setViewId(null);
              setShowDocRegister(true);
            }}
          >
            Add Document
          </Button>
        }
      >
        <FileDragDrop onFilesDrop={handleDrop} uploadRef={uploadRef}>
          <Grid
            data={process(list.data || [], dataState)}
            onDataStateChange={dataStateChange}
            className={`fs-md pb-1 ${component === "supplier" ? styles.supplierGrid : styles.clientGrid}`}
            sortable={true}
            resizable={true}
            {...dataState}
          >
            <GridColumn
              field="date"
              title="Date"
              width="120px"
              cell={DateFormatCell}
              className="gridBoldField ps-4"
              headerClassName="ps-4"
            />
            <GridColumn field="details" title="Details" />
            <GridColumn
              field="documentId"
              title="Document"
              cell={DocumentCell}
              width="180px"
            />
            <GridColumn field="" title="  " width="78px" cell={ViewDocs} />
            <GridColumn
              field=""
              title=" "
              width="40x"
              cell={DeleteDocCell}
              headerClassName="text-center"
            />
          </Grid>
        </FileDragDrop>
      </Tile>

      {showDocRegister && (
        <ClientDocumentModal
          setshowMatterForm={() => {
            setViewId(null);
            setShowDocRegister(false);
            if (droppedFiles.length) setDroppedFiles([]);
          }}
          droppedFiles={droppedFiles}
          docId={viewId}
          updateData={() => {
            setViewId(null);
            setShowDocRegister(false);
            if (droppedFiles.length) setDroppedFiles([]);
            fetchDocs();
          }}
          clientId={isSupplier ? undefined : clientId}
          supplierId={isSupplier ? supplierId : undefined}
          supplierRef={isSupplier ? supplierRef : undefined}
          contactId={isContact ? contactId : undefined}
          component={component}
          canModify={canModify}
        />
      )}

      {showDocument && (
        <DocumentViewer
          data={documentViewerData}
          onClose={() => {
            setShowDocument(false);
            setDocumentViewerData([]);
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content={`Are you sure you want to remove this document from the ${component === "supplier"
            ? "Supplier"
            : component === "contact"
              ? "Contact"
              : "Client"
            }?`}
          onClickYes={confirmDeleteDocument}
          onClickNo={() => {
            setShowDeleteConfirm(false);
            setDocRowToDelete(null);
          }}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocRowToDelete(null);
          }}
          isLoading={deletingDoc}
        />
      )}
    </div>
  );
};
